


































































































































import {Component, Vue, Watch} from 'vue-property-decorator'
import {$} from '@/facade'
import {OrbitProject} from '@/model/resource/OrbitProject'
import {getN3InvocationBuilder} from '@/model/wallets/invocationsBuilder'
import {OrbitAddProjectStep} from '@/enums/OrbitAddProjectStep'
import {NeoHelper} from '@/helpers/NeoHelper'
import OrbitAddProjectFormStep from '@/components/orbit/OrbitAddProjectFormStep.vue'
import OrbitPersistProjectReviewFormStep from '@/components/orbit/OrbitPersistProjectReviewFormStep.vue'
import OrbitLoadingTransactionStep from '@/components/orbit/OrbitLoadingTransactionStep.vue'
import OrbitSuccessStep from '@/components/orbit/OrbitSuccessStep.vue'
import OrbitInsufficientGasStep from '@/components/orbit/OrbitInsufficientGasStep.vue'
import OrbitUnexpectedErrorStep from '@/components/orbit/OrbitUnexpectedErrorStep.vue'
import OrbitRejectedByTheUserStep from '@/components/orbit/OrbitRejectedByTheUserStep.vue'
import OrbitTokenNotWhitelistedStep from '@/components/orbit/OrbitTokenNotWhitelistedStep.vue'
import {OrbitHelper} from '@/helpers/OrbitHelper'
import {DateHelper} from '@/helpers/DateHelper'

@Component({
  computed: {
    OrbitAddProjectStep() {
      return OrbitAddProjectStep
    },
  },
  components: {
    OrbitAddProjectFormStep,
    OrbitPersistProjectReviewFormStep,
    OrbitSuccessStep,
    OrbitLoadingTransactionStep,
    OrbitInsufficientGasStep,
    OrbitUnexpectedErrorStep,
    OrbitRejectedByTheUserStep,
    OrbitTokenNotWhitelistedStep,
  },
})
export default class OrbitAddProjectModal extends Vue {
  orbitProject: OrbitProject = new OrbitProject()
  currentStep: OrbitAddProjectStep = OrbitAddProjectStep.FORM
  transactionHash: string | null = null

  mounted() {
    this.populateResources()
  }

  async populateResources() {
    await OrbitHelper.checkWalletPermissions().catch(() => {
      this.closeModal()
    })
  }

  async addProject() {
    try {
      if (
        !this.orbitProject.contract ||
        !this.orbitProject.contract.hash ||
        !this.orbitProject.amount ||
        !this.orbitProject.tokenPriced ||
        !this.orbitProject.tokenPriced.hash
      ) {
        throw new Error('Missing parameters')
      }

      const n3InvocationBuilder = getN3InvocationBuilder()
      const addOrbitProjectInvocationBuilder = n3InvocationBuilder.addOrbitProjectInvocation(
        {
          projectScriptHash: this.orbitProject.contract.hash,
          assetType: '17',
          amount: String(this.orbitProject.amount),
          tokenScriptHash: this.orbitProject.tokenPriced.hash,
          eventName: this.orbitProject.eventNames ?? '',
        }
      )

      this.currentStep = OrbitAddProjectStep.WAITING_SIGNATURE

      this.transactionHash = await $.walletAdapter.invokeN3Wallet(
        addOrbitProjectInvocationBuilder
      )

      if (!this.transactionHash) throw new Error('Undefined transaction')

      this.currentStep = OrbitAddProjectStep.VALIDATING_TRANSACTION

      const isTransactionValid = await NeoHelper.validateTransaction(
        this.transactionHash
      )
      if (!isTransactionValid) throw new Error('Failed to verify transaction')

      this.orbitProject.transactionHash = this.transactionHash
      this.orbitProject.instanceId = Number(
        await OrbitHelper.getProjectInstanceId(this.orbitProject.contract.hash)
      )

      if (this.orbitProject.createdAt) {
        const createdAtTimestamp =
          this.orbitProject.createdAt instanceof Date
            ? this.orbitProject.createdAt.getTime()
            : typeof this.orbitProject.createdAt === 'string'
            ? new Date(this.orbitProject.createdAt).getTime()
            : Date.now()
        this.orbitProject.createdAt = DateHelper.formatDateForBackend(
          createdAtTimestamp
        ) as any
      } else {
        this.orbitProject.createdAt = DateHelper.formatDateForBackend(
          Date.now()
        ) as any
      }

      this.orbitProject.active = true
      this.orbitProject.orbitRound = null

      await this.orbitProject.persistOrbitProject()

      this.currentStep = OrbitAddProjectStep.ADDED

      $.toast.success('system.success.persist')
    } catch (error) {
      this.threatError(error)
      return
    }

    this.closeModal()
  }

  onClickCloseModal() {
    if (
      this.currentStep === OrbitAddProjectStep.WAITING_SIGNATURE ||
      this.currentStep === OrbitAddProjectStep.VALIDATING_TRANSACTION
    ) {
      return
    }

    this.closeModal()
  }

  closeModal() {
    this.currentStep = OrbitAddProjectStep.FORM
    this.orbitProject = new OrbitProject()
    this.$emit('close')
  }

  goToReviewFormStep(orbitProject: OrbitProject) {
    this.orbitProject = orbitProject
    this.currentStep = OrbitAddProjectStep.REVIEW_FORM
  }

  @Watch('$store.state.walletAdapter.n3.address')
  async onN3AddressChange(address: string | null) {
    if (!address) {
      this.closeModal()
      return
    }

    await OrbitHelper.checkWalletPermissions().catch(() => {
      this.closeModal()
    })
  }

  private describeError(error: any): string {
    if (!error) return ''

    if (typeof error === 'string') return error

    const parts: string[] = [error.message, error.description].filter(Boolean)

    try {
      parts.push(JSON.stringify(error))
    } catch {
      parts.push(String(error))
    }

    return parts.join(' ')
  }

  private threatError(error: any) {
    console.error(error)

    const isTokenNotWhitelisted = this.describeError(error)
      .toLowerCase()
      .includes('token not whitelisted')

    if (isTokenNotWhitelisted) {
      this.currentStep = OrbitAddProjectStep.TOKEN_NOT_WHITELISTED
      return
    }

    const isNeonWalletRejected =
      error.message && String(error.message).includes('rejected by the user')

    const isNeoLineWalletRejected =
      error.description &&
      error.description === 'The user cancels, or refuses the dapps request'

    if (isNeonWalletRejected || isNeoLineWalletRejected) {
      this.currentStep = OrbitAddProjectStep.REJECTED_BY_THE_USER
      return
    }

    const hasInsufficientFunds =
      error.message && String(error.message).includes('insufficient funds')
    if (hasInsufficientFunds) {
      this.currentStep = OrbitAddProjectStep.INSUFFICIENT_GAS
      return
    }

    this.currentStep = OrbitAddProjectStep.UNKNOWN_ERROR
  }
}
